import { useCallback, useEffect, useRef, useState } from 'react';

import Button from '@/components/v4/Button';
import { ModalXIconButton } from '@/components/v4/Modal/ModalPaper';

import { ArrowDownIcon, BusinessNumberEditCard, DocumentSignatureIcon } from '../shared';

const CAUTION_ITEMS: React.ReactNode[] = [
  '신청 내용은 쿠팡이츠에서 확인 후 처리됩니다',
  '처리까지는 접수일 기준 영업일 +2일 정도 소요될 수 있습니다',
  '사업자번호 등 입력한 정보에 오타 또는 누락이 있을 경우 정상 접수가 되지 않을 수 있습니다',
  '내용이 없는 리뷰 또는 게시중단 후 재게시된 리뷰는 신청이 제한됩니다',
  '검토 결과에 따라 블라인드 처리가 진행되지 않을 수 있습니다',
  <>
    블라인드 처리가 완료될 경우 쿠팡이츠에서 처리 완료 문자가 발송되며, 동시에{' '}
    <span className="text-cds-orange-60 font-semibold">
      고객에게도 이의신청 접수를 위한 문자가 발송될 수 있습니다
    </span>
  </>,
];

export const CpeatsCautionContent = ({
  onNext,
  onClose,
}: {
  onNext: () => void;
  onClose: () => void;
}) => {
  const [isEditing, setIsEditing] = useState(false);

  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const bottomRef = useRef<HTMLDivElement>(null);
  const [isScrolledToBottom, setIsScrolledToBottom] = useState(false);
  const [hasScrollableContent, setHasScrollableContent] = useState(false);

  const checkScrollPosition = useCallback(() => {
    const container = scrollContainerRef.current;
    if (!container) return;
    const { scrollTop, scrollHeight, clientHeight } = container;
    setHasScrollableContent(scrollHeight > clientHeight);
    setIsScrolledToBottom(
      scrollHeight <= clientHeight || scrollHeight - scrollTop - clientHeight < 10,
    );
  }, []);

  useEffect(() => {
    setIsScrolledToBottom(false);
    setTimeout(checkScrollPosition, 100);
  }, [checkScrollPosition]);

  return (
    <div className="flex flex-col relative h-[80vh] max-h-[700px] md:h-[700px]">
      <div className="flex items-center justify-between px-5 py-2.5 mt-2.5 shrink-0">
        <p className="text-black font-semibold text-lg">리뷰 블라인드 신청 전 확인</p>
        <ModalXIconButton onClick={onClose} dataGtmId="blind-caution-close" />
      </div>

      <div
        ref={scrollContainerRef}
        className="flex-1 overflow-y-auto px-5 md:px-20 pb-40 pt-1 md:pt-2.5"
        onScroll={checkScrollPosition}
      >
        <div className="flex flex-col items-center mb-7">
          <DocumentSignatureIcon />
          <p className="text-cds-black-99 font-semibold text-lg leading-[140%] mt-3 text-center">
            블라인드 신청 전
          </p>
          <p className="text-cds-black-99 font-semibold text-lg leading-[140%] text-center">
            꼭 확인해주세요
          </p>
          <p className="text-cds-coolgray-65 text-sm mt-3 text-center tracking-[-0.154px]">
            사업자번호가 틀리면 블라인드 신청이 어려워요
          </p>
        </div>

        <div className="flex flex-col gap-3">
          <div className="border border-cds-coolgray-15 rounded-2xl py-3 px-3.5">
            <p className="text-cds-coolgray-85 text-sm font-medium leading-[150%] mb-2">
              확인해주세요!
            </p>
            <ul className="list-disc pl-[18px] flex flex-col gap-0.5">
              {CAUTION_ITEMS.map((item, index) => (
                <li
                  key={index}
                  className="text-cds-coolgray-60 text-xs md:text-sm font-normal leading-[150%]"
                >
                  {item}
                </li>
              ))}
            </ul>
          </div>

          <BusinessNumberEditCard showWarning onEditingChange={setIsEditing} />
        </div>
        <div ref={bottomRef} />
      </div>

      <div className="absolute bottom-0 left-0 right-0 mx-5 md:mx-5">
        <div
          className="px-5 md:px-20"
          style={{
            background: 'linear-gradient(180deg, rgba(255, 255, 255, 0.00) 0%, #FFF 90%)',
          }}
        >
          {hasScrollableContent && !isScrolledToBottom ? (
            <Button
              variant="contained"
              color="orange"
              className="w-full rounded-xl flex items-center justify-center gap-2"
              height={40}
              onClick={() => bottomRef.current?.scrollIntoView({ behavior: 'smooth' })}
              data-gtm-id="blind-caution-end"
            >
              끝까지 확인하기 <ArrowDownIcon />
            </Button>
          ) : (
            <div className="flex gap-2.5">
              <Button
                className="w-[88px] bg-cds-coolgray-10 text-cds-coolgray-80 rounded-xl shrink-0"
                height={40}
                onClick={onClose}
                data-gtm-id="blind-caution-cancel"
              >
                취소
              </Button>
              <Button
                variant="contained"
                color="orange"
                className="flex-1 rounded-xl text-white"
                height={40}
                onClick={onNext}
                disabled={isEditing}
                data-gtm-id="blind-caution-confirm"
              >
                확인했어요 (1/2) →
              </Button>
            </div>
          )}
        </div>
        <div className="h-6 bg-white" />
      </div>
    </div>
  );
};
